import dayjs, { type Dayjs } from "dayjs"
import { useEffect, useRef, useState } from "react"

import { useAppSelector } from "../../../../../hooks/use-app-selector.ts"
import { readAllDayDate } from "../../../../../utils/date/get-date-safe.ts"
import {
  selectActiveBranchId,
  selectIsCompanyMode,
  selectPageFilter
} from "../../../../Branches/redux/branches.slice.ts"
import * as calendarServices from "../../../api/services.ts"
import { type CalendarFilterParticipant } from "../../../redux/calendar.types.ts"
import { type Event } from "../../../types/types.ts"

const DAY_KEY_FORMAT = "YYYY-MM-DD"
// A single bad or open-ended record must not turn one event into a
// month-long scan — event days beyond this span are simply not dotted.
const MAX_SPAN_DAYS = 62

/** The local calendar day an event starts and ends on, all-day-aware. */
const eventDayRange = (event: Event, timezone: string) => {
  const start = event?.all_day
    ? readAllDayDate(event.start_at)
    : dayjs(event?.start_at).tz(timezone)
  if (!start?.isValid()) return null

  const end = event?.end_at
    ? event?.all_day
      ? readAllDayDate(event.end_at, event.start_at)
      : dayjs(event.end_at).tz(timezone)
    : start

  return { start, end: end?.isValid() ? end : start }
}

const filterKeyOf = (filterParticipants: CalendarFilterParticipant[] | null) =>
  filterParticipants?.length
    ? [...filterParticipants]
        .map((p) => p.value)
        .sort()
        .join(",")
    : ""

// The mini-month always shows a full 6-week grid regardless of the main
// view's own date range — a Week view only ever fetches ~1-2 weeks, so
// asking "does this day anywhere in the grid have an event" from whatever
// the main view happened to fetch produced dots that silently changed
// depending on which view you'd last been on, or vanished outside that
// window entirely. This hook is the mini-month's own, self-sufficient answer
// to that question: one request for the whole displayed grid, independent of
// the main calendar's own fetch.
export function useMiniMonthEventDays({
  displayedMonth,
  timezone,
  filterParticipants,
  refreshSignal
}: {
  displayedMonth: Dayjs
  timezone: string
  filterParticipants: CalendarFilterParticipant[] | null
  // Folded into the cache key below so an event created/edited/deleted
  // elsewhere on the page busts a month already cached as "no events" or
  // "events on the wrong day" — the cache has no other way to learn that.
  refreshSignal?: string
}): { eventDays: Set<string>; loading: boolean } {
  const [eventDays, setEventDays] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)

  // Keyed by month + filter + refreshSignal, so re-visiting a month already
  // fetched under the same filter (with nothing changed since) is instant —
  // no flicker, no stale-then-corrected dots — while a filter change or a
  // real mutation elsewhere never reads a stale cached answer.
  const cacheRef = useRef<Map<string, Set<string>>>(new Map())
  // Bumped on every request; a response only commits if it's still the most
  // recent one asked for. Without this, browsing month-to-month quickly (the
  // exact scenario reported — "September request, user clicks October,
  // September response arrives late") could let a late, stale response
  // overwrite the month the user is actually looking at now.
  const requestIdRef = useRef(0)

  // Same Branches-filter precedence as the main grid and Team Availability
  // — folded into the cache key too, so switching branches never shows
  // dots cached under a different scope.
  const isCompanyMode = useAppSelector(selectIsCompanyMode)
  const activeBranchId = useAppSelector(selectActiveBranchId)
  const pageBranchScope = useAppSelector(selectPageFilter("calendar"))
  const branchScopeParams: { team?: string; teams?: string } = !isCompanyMode
    ? activeBranchId
      ? { team: activeBranchId }
      : {}
    : pageBranchScope.type === "subset"
      ? { teams: pageBranchScope.teamIds.join(",") }
      : {}
  const branchScopeKey = branchScopeParams.team ?? branchScopeParams.teams ?? "all"

  const filterKey = filterKeyOf(filterParticipants)
  const monthKey = displayedMonth.format("YYYY-MM")
  const cacheKey = `${monthKey}|${filterKey}|${branchScopeKey}|${refreshSignal ?? ""}`

  useEffect(() => {
    const cached = cacheRef.current.get(cacheKey)
    if (cached) {
      setEventDays(cached)
      setLoading(false)
      return
    }

    const requestId = ++requestIdRef.current
    setLoading(true)

    // The full 6-week grid the mini-month actually renders, not just the
    // calendar month — a leading/trailing day from the adjacent month can
    // still show its own dot.
    const gridStart = displayedMonth.startOf("month").startOf("week")
    const gridEnd = displayedMonth.endOf("month").endOf("week")

    calendarServices
      .getEvents({
        start: gridStart.format(),
        end: gridEnd.format(),
        // No participants filter at all (rather than an empty string) when
        // nothing is selected — the backend's own default already resolves
        // to everything this user is permitted to see, the same scope the
        // main calendar itself gets with no filter applied. This is
        // deliberately the same request shape the main calendar and
        // Availability already use — no second authorization model here.
        ...(filterKey ? { participants: filterKey } : {}),
        ...branchScopeParams
      })
      .then((response) => {
        if (requestId !== requestIdRef.current) return
        if (!response.ok) {
          setLoading(false)
          return
        }

        const days = new Set<string>()
        ;(response.data?.results || []).forEach((event) => {
          const range = eventDayRange(event, timezone)
          if (!range) return

          let cursor = range.start
          let guard = 0
          while (!cursor.isAfter(range.end, "day") && guard < MAX_SPAN_DAYS) {
            days.add(cursor.format(DAY_KEY_FORMAT))
            cursor = cursor.add(1, "day")
            guard += 1
          }
        })

        cacheRef.current.set(cacheKey, days)
        if (requestId === requestIdRef.current) {
          setEventDays(days)
          setLoading(false)
        }
      })
  }, [cacheKey, timezone])

  return { eventDays, loading }
}
